import React, { useState, useEffect, useCallback, useRef } from 'react';
import Swal from 'sweetalert2';
import './GroupInfo.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://37.60.253.184:8081';

const getAuthToken = (token) =>
  token || (() => {
    try { return JSON.parse(localStorage.getItem('insafUser'))?.token; } catch { return null; }
  })();

const getAvatarUrl = img => {
  if (!img) return null;
  if (img.startsWith('http')) {
    return img.replace('http://37.60.253.184:8081', process.env.REACT_APP_API_URL || 'http://localhost:3001')
              .replace('https://37.60.253.184:8081', process.env.REACT_APP_API_URL || 'http://localhost:3001');
  }
  return `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/${img}`;
};

const GroupInfo = ({ group, userData, token, onBack, onGroupDeleted, onGroupLeft, onGroupUpdated }) => {
  const [groupName, setGroupName]       = useState(group.name || '');
  const [description, setDescription]   = useState(group.description || '');
  const [members, setMembers]           = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [updating, setUpdating]         = useState(false);
  const [groupImage, setGroupImage]     = useState(group.group_image || null);
  const imageInputRef = useRef(null);

  // ── Manage-members modal state ──────────────────────────────────────────
  const [showManageModal, setShowManageModal]   = useState(false);
  const [allLawyers, setAllLawyers]             = useState([]);
  const [loadingLawyers, setLoadingLawyers]     = useState(false);
  const [manageSearch, setManageSearch]         = useState('');
  const [selectedToAdd, setSelectedToAdd]       = useState([]);
  const [addingMembers, setAddingMembers]       = useState(false);
  const [removingId, setRemovingId]             = useState(null);

  const authToken = getAuthToken(token);
  const avatarUrl = groupImage ? getAvatarUrl(groupImage) : null;
  const initials  = groupName?.charAt(0)?.toUpperCase() || 'G';

  // created_by may be a full user object (from getGroups) or a plain ID (from getGroupDetails)
  const creatorId  = group.created_by?.id ?? group.created_by;
  const isCreator  = String(userData?.id) === String(creatorId);

  // ── Fetch group details + members ─────────────────────────────────────────
  const fetchGroupDetails = useCallback(async () => {
    try {
      setLoadingMembers(true);
      const res  = await fetch(`${API_BASE}/api/chat/groups/${group.id}`, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });
      const data = await res.json();
      if (res.ok && data.success && data.data) {
        const g = data.data.group || data.data;
        setGroupName(g.name || group.name || '');
        setDescription(g.description || '');
        setGroupImage(g.group_image || null);
        const membersList = g.members || data.data.members || [];
        setMembers(membersList);
      }
    } catch (err) {
      console.error('[GroupInfo] fetchGroupDetails error:', err);
    } finally {
      setLoadingMembers(false);
    }
  }, [group.id, authToken, group.name]);

  useEffect(() => { fetchGroupDetails(); }, [fetchGroupDetails]);

  // ── Fetch all lawyers (for Manage modal) ─────────────────────────────────
  const fetchAllLawyers = useCallback(async () => {
    try {
      setLoadingLawyers(true);
      const res  = await fetch(`${API_BASE}/api/guests/getLawyers`, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });
      const data = await res.json();
      if (res.ok && data.data) {
        const list = Array.isArray(data.data) ? data.data : [data.data];
        setAllLawyers(list);
      }
    } catch (err) {
      console.error('[GroupInfo] fetchAllLawyers error:', err);
    } finally {
      setLoadingLawyers(false);
    }
  }, [authToken]);

  const openManageModal = () => {
    setSelectedToAdd([]);
    setManageSearch('');
    setShowManageModal(true);
    fetchAllLawyers();
  };

  // ── Add selected lawyers to group ─────────────────────────────────────────
  const handleAddMembers = async () => {
    if (selectedToAdd.length === 0) return;
    try {
      setAddingMembers(true);
      const res  = await fetch(`${API_BASE}/api/chat/groups/${group.id}/add-members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ member_ids: selectedToAdd.map(l => l.id) }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        Swal.fire({ title: 'Done!', text: 'Members added successfully.', icon: 'success', confirmButtonColor: '#2c3e50', timer: 1500, showConfirmButton: false });
        setSelectedToAdd([]);
        await fetchGroupDetails();   // refresh members list
      } else {
        Swal.fire({ title: 'Error', text: data.message || 'Failed to add members', icon: 'error', confirmButtonColor: '#2c3e50' });
      }
    } catch (err) {
      console.error('[GroupInfo] addMembers error:', err);
      Swal.fire({ title: 'Error', text: 'Failed to add members', icon: 'error', confirmButtonColor: '#2c3e50' });
    } finally {
      setAddingMembers(false);
    }
  };

  // ── Remove a member from group ────────────────────────────────────────────
  const handleRemoveMember = async (memberId, memberName) => {
    const result = await Swal.fire({
      title: `Remove ${memberName}?`,
      text: 'They will be removed from this group.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove',
    });
    if (!result.isConfirmed) return;
    try {
      setRemovingId(memberId);
      const res  = await fetch(`${API_BASE}/api/chat/groups/${group.id}/remove-member/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchGroupDetails();
      } else {
        Swal.fire({ title: 'Error', text: data.message || 'Failed to remove member', icon: 'error', confirmButtonColor: '#2c3e50' });
      }
    } catch (err) {
      console.error('[GroupInfo] removeMember error:', err);
    } finally {
      setRemovingId(null);
    }
  };

  // ── Update group name + description ───────────────────────────────────────
  const handleUpdate = async () => {
    if (!groupName.trim()) {
      Swal.fire({ title: 'Warning', text: 'Group name cannot be empty', icon: 'warning', confirmButtonColor: '#2c3e50' });
      return;
    }
    try {
      setUpdating(true);
      const res = await fetch(`${API_BASE}/api/chat/groups/${group.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ name: groupName.trim(), description: description.trim() }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        Swal.fire({ title: 'Updated!', text: 'Group info updated successfully.', icon: 'success', confirmButtonColor: '#2c3e50' });
        if (onGroupUpdated) onGroupUpdated({ ...group, name: groupName.trim(), description: description.trim() });
      } else {
        Swal.fire({ title: 'Error', text: data.message || 'Failed to update group', icon: 'error', confirmButtonColor: '#2c3e50' });
      }
    } catch (err) {
      console.error('[GroupInfo] update error:', err);
      Swal.fire({ title: 'Error', text: 'Failed to update group', icon: 'error', confirmButtonColor: '#2c3e50' });
    } finally {
      setUpdating(false);
    }
  };

  // ── Update group image (uses PUT /api/chat/groups/{id} with group_image field) ──
  const handleImageChange = async (file) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('group_image', file);
      // Laravel doesn't support PUT with multipart — use POST + _method spoofing
      formData.append('_method', 'PUT');
      const res = await fetch(`${API_BASE}/api/chat/groups/${group.id}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: formData,
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const newImg = data.data?.group?.group_image
          || data.data?.group_image_url
          || null;
        setGroupImage(newImg);
        if (onGroupUpdated) onGroupUpdated({ ...group, group_image: newImg });
        Swal.fire({ title: 'Done!', text: 'Group photo updated.', icon: 'success', confirmButtonColor: '#2c3e50', timer: 1500, showConfirmButton: false });
      } else {
        Swal.fire({ title: 'Error', text: data.message || 'Failed to update image', icon: 'error', confirmButtonColor: '#2c3e50' });
      }
    } catch (err) {
      console.error('[GroupInfo] image upload error:', err);
      Swal.fire({ title: 'Error', text: 'Failed to update image', icon: 'error', confirmButtonColor: '#2c3e50' });
    }
  };

  // ── Leave group ───────────────────────────────────────────────────────────
  const handleLeave = async () => {
    const result = await Swal.fire({
      title: 'Leave Group?',
      text: 'You will no longer receive messages from this group.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, leave',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await fetch(`${API_BASE}/api/chat/groups/${group.id}/leave`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        Swal.fire({ title: 'Left!', text: 'You have left the group.', icon: 'success', confirmButtonColor: '#2c3e50' });
        if (onGroupLeft) onGroupLeft(group.id);
      } else {
        Swal.fire({ title: 'Error', text: data.message || 'Failed to leave group', icon: 'error', confirmButtonColor: '#2c3e50' });
      }
    } catch (err) {
      console.error('[GroupInfo] leave error:', err);
      Swal.fire({ title: 'Error', text: 'Failed to leave group', icon: 'error', confirmButtonColor: '#2c3e50' });
    }
  };

  // ── Delete group ──────────────────────────────────────────────────────────
  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Delete Group?',
      text: 'This will permanently delete the group and all its messages.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await fetch(`${API_BASE}/api/chat/groups/${group.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        Swal.fire({ title: 'Deleted!', text: 'Group deleted successfully.', icon: 'success', confirmButtonColor: '#2c3e50' });
        if (onGroupDeleted) onGroupDeleted(group.id);
      } else {
        Swal.fire({ title: 'Error', text: data.message || 'Failed to delete group', icon: 'error', confirmButtonColor: '#2c3e50' });
      }
    } catch (err) {
      console.error('[GroupInfo] delete error:', err);
      Swal.fire({ title: 'Error', text: 'Failed to delete group', icon: 'error', confirmButtonColor: '#2c3e50' });
    }
  };

  return (
    <>
    <div className="gi-container">
      {/* Hidden image input */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={e => {
          const f = e.target.files[0];
          if (f) handleImageChange(f);
          e.target.value = '';
        }}
      />

      {/* ── Header bar ── */}
      <div className="gi-header">
        <button className="gi-back-btn" onClick={onBack}>‹</button>
        <span className="gi-header-title">Group Info</span>
        <div style={{ width: 40 }} />
      </div>

      <div className="gi-scroll">
        {/* ── Avatar ── */}
        <div className="gi-avatar-section">
          <div
            className="gi-avatar-wrap"
            onClick={() => isCreator && imageInputRef.current?.click()}
            style={{ cursor: isCreator ? 'pointer' : 'default' }}
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt={groupName} className="gi-avatar-img" onError={e => { e.target.style.display = 'none'; }} />
            ) : (
              <div className="gi-avatar-placeholder">{initials}</div>
            )}
            {isCreator && <div className="gi-avatar-edit-badge">✏️</div>}
          </div>
          {isCreator && <p className="gi-avatar-hint">Tap to change photo</p>}
        </div>

        {/* ── Group name + description ── */}
        <p className="gi-label">Group Name</p>
        <div className="gi-name-display">{groupName}</div>

        <p className="gi-label">Description</p>
        <div className="gi-desc-display">{description || <span className="gi-placeholder-text">No description</span>}</div>

        {/* ── Action buttons (Manage / Leave / Delete) ── */}
        <div className="gi-actions">
          {isCreator && (
            <button className="gi-action-btn" onClick={openManageModal}>
              <span className="gi-action-icon">👥</span>
              <span className="gi-action-label">Manage</span>
            </button>
          )}
          {!isCreator && (
            <button className="gi-action-btn gi-action-btn--warn" onClick={handleLeave}>
              <span className="gi-action-icon">🚪</span>
              <span className="gi-action-label">Leave</span>
            </button>
          )}
          {isCreator && (
            <button className="gi-action-btn gi-action-btn--danger" onClick={handleDelete}>
              <span className="gi-action-icon">🗑️</span>
              <span className="gi-action-label">Delete</span>
            </button>
          )}
        </div>

        {/* ── Edit fields (creator only) ── */}
        {isCreator ? (
          <>
            <p className="gi-label">Group Name</p>
            <input
              className="gi-input"
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
              maxLength={80}
              placeholder="Enter group name"
            />

            <p className="gi-label">Description</p>
            <textarea
              className="gi-textarea"
              value={description}
              onChange={e => setDescription(e.target.value)}
              maxLength={300}
              placeholder="Enter description"
              rows={3}
            />

            {/* ── Update button ── */}
            <button
              className="gi-update-btn"
              onClick={handleUpdate}
              disabled={updating || !groupName.trim()}
            >
              {updating ? 'Updating…' : 'Update'}
            </button>
          </>
        ) : null}
        {/* ── Members list ── */}
        <p className="gi-label gi-members-label">
          Members {members.length > 0 ? `(${members.length})` : ''}
        </p>
        <div className="gi-members-list">
          {loadingMembers ? (
            <p className="gi-members-loading">Loading members…</p>
          ) : members.length === 0 ? (
            <p className="gi-members-empty">No members found</p>
          ) : (
            members.map(member => {
              // API shape: { id, user: {id, first_name, last_name, profile_image}, role, joined_at }
              const u = member.user || member;
              const memberAvatar = u.profile_image ? getAvatarUrl(u.profile_image) : null;
              const memberInitials = (u.first_name?.charAt(0) || u.name?.charAt(0) || 'U').toUpperCase();
              const fullName = u.first_name
                ? `${u.first_name} ${u.last_name || ''}`.trim()
                : u.name || `User ${u.id}`;
              const isMe = String(u.id) === String(userData?.id);
              const isAdmin = member.role === 'admin' || member.is_admin || member.pivot?.role === 'admin';

              return (
                <div key={u.id ?? member.id} className="gi-member-item">
                  <div className="gi-member-avatar">
                    {memberAvatar ? (
                      <img src={memberAvatar} alt={fullName} onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="gi-member-avatar-placeholder">{memberInitials}</div>
                    )}
                  </div>
                  <div className="gi-member-info">
                    <p className="gi-member-name">
                      {fullName}
                      {isMe && <span className="gi-member-you"> (You)</span>}
                    </p>
                    <p className="gi-member-role">
                      {isAdmin ? '⭐ Admin' : '👤 Member'}
                    </p>
                  </div>
                  {isCreator && !isMe && !isAdmin && (
                    <button
                      className="gi-remove-btn"
                      onClick={() => handleRemoveMember(u.id, fullName)}
                      disabled={removingId === u.id}
                      title="Remove member"
                    >
                      {removingId === u.id ? '…' : '✕'}
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>

      {/* ══ Manage Members Modal ══ */}
      {showManageModal && (() => {
        // IDs of users already in the group — use member.user.id (not pivot row id)
        const existingUserIds = new Set(
          members.map(m => String(m.user ? m.user.id : m.id))
        );
        // Also exclude the current logged-in user (they're the admin)
        existingUserIds.add(String(userData?.id));

        // Lawyers NOT yet in the group (available to add)
        const available = allLawyers.filter(l => !existingUserIds.has(String(l.id)));

        // Apply search across both lists
        const q = manageSearch.toLowerCase().trim();
        const filteredAvailable = q
          ? available.filter(l =>
              (l.first_name && l.first_name.toLowerCase().includes(q)) ||
              (l.last_name  && l.last_name.toLowerCase().includes(q))  ||
              (l.mobile_number && l.mobile_number.includes(manageSearch))
            )
          : available;

        const filteredMembers = q
          ? members.filter(m => {
              const u = m.user || m;
              return (
                (u.first_name && u.first_name.toLowerCase().includes(q)) ||
                (u.last_name  && u.last_name.toLowerCase().includes(q))
              );
            })
          : members;

        return (
          <div className="gi-modal-overlay" onClick={() => setShowManageModal(false)}>
            <div className="gi-modal" onClick={e => e.stopPropagation()}>

              {/* Modal header */}
              <div className="gi-modal-header">
                <h3 className="gi-modal-title">Manage Members</h3>
                <button className="gi-modal-close" onClick={() => setShowManageModal(false)}>✕</button>
              </div>

              {/* Search */}
              <div className="gi-modal-search-wrap">
                <span className="gi-modal-search-icon">🔍</span>
                <input
                  className="gi-modal-search"
                  placeholder="Search by name or phone…"
                  value={manageSearch}
                  onChange={e => setManageSearch(e.target.value)}
                />
              </div>

              <div className="gi-modal-body">

                {/* ── Current members ── */}
                <p className="gi-modal-section-label">
                  Current Members ({filteredMembers.length})
                </p>
                {filteredMembers.length === 0 ? (
                  <p className="gi-modal-empty">No members match search</p>
                ) : (
                  filteredMembers.map(member => {
                    const u        = member.user || member;
                    const mAvatar  = u.profile_image ? getAvatarUrl(u.profile_image) : null;
                    const mInit    = (u.first_name?.charAt(0) || 'U').toUpperCase();
                    const mName    = u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : `User ${u.id}`;
                    const mIsMe    = String(u.id) === String(userData?.id);
                    const mIsAdmin = member.role === 'admin' || member.is_admin;

                    return (
                      <div key={u.id} className="gi-modal-member-row">
                        <div className="gi-modal-member-avatar">
                          {mAvatar
                            ? <img src={mAvatar} alt={mName} onError={e => { e.target.style.display = 'none'; }} />
                            : <div className="gi-modal-member-init">{mInit}</div>
                          }
                        </div>
                        <div className="gi-modal-member-info">
                          <p className="gi-modal-member-name">
                            {mName}{mIsMe && <span className="gi-member-you"> (You)</span>}
                          </p>
                          <p className="gi-modal-member-role">{mIsAdmin ? '⭐ Admin' : '👤 Member'}</p>
                        </div>
                        {!mIsMe && !mIsAdmin && (
                          <button
                            className="gi-modal-remove-btn"
                            onClick={() => handleRemoveMember(u.id, mName)}
                            disabled={removingId === u.id}
                          >
                            {removingId === u.id ? '…' : 'Remove'}
                          </button>
                        )}
                      </div>
                    );
                  })
                )}

                <hr className="gi-modal-section-divider" />

                {/* ── Add new members ── */}
                <p className="gi-modal-section-label">
                  Add Lawyers
                </p>

                {loadingLawyers ? (
                  <p className="gi-modal-empty">Loading lawyers…</p>
                ) : filteredAvailable.length === 0 ? (
                  <p className="gi-modal-empty">
                    {available.length === 0 ? 'All lawyers are already members' : 'No lawyers match search'}
                  </p>
                ) : (
                  <>
                    {selectedToAdd.length > 0 && (
                      <div className="gi-modal-selected-banner">
                        ✓ {selectedToAdd.length} selected
                        <button className="gi-modal-clear-btn" onClick={() => setSelectedToAdd([])}>Clear</button>
                      </div>
                    )}
                    {filteredAvailable.map(lawyer => {
                      const lAvatar   = lawyer.profile_image ? getAvatarUrl(lawyer.profile_image) : null;
                      const lInit     = (lawyer.first_name?.charAt(0) || 'L').toUpperCase();
                      const lName     = `${lawyer.first_name || ''} ${lawyer.last_name || ''}`.trim();
                      const isSelected = selectedToAdd.some(s => s.id === lawyer.id);

                      const toggle = () => {
                        if (isSelected) setSelectedToAdd(prev => prev.filter(s => s.id !== lawyer.id));
                        else setSelectedToAdd(prev => [...prev, lawyer]);
                      };

                      return (
                        <div
                          key={lawyer.id}
                          className={`gi-modal-lawyer-row${isSelected ? ' gi-modal-lawyer-row--selected' : ''}`}
                          onClick={toggle}
                        >
                          <input
                            type="checkbox"
                            className="gi-modal-checkbox"
                            checked={isSelected}
                            onChange={toggle}
                            onClick={e => e.stopPropagation()}
                          />
                          <div className="gi-modal-member-avatar">
                            {lAvatar
                              ? <img src={lAvatar} alt={lName} onError={e => { e.target.style.display = 'none'; }} />
                              : <div className="gi-modal-member-init">{lInit}</div>
                            }
                          </div>
                          <div className="gi-modal-member-info">
                            <p className="gi-modal-member-name">{lName}</p>
                            <p className="gi-modal-member-role">{lawyer.mobile_number || ''}</p>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>

              {/* Footer */}
              {selectedToAdd.length > 0 && (
                <div className="gi-modal-footer">
                  <button
                    className="gi-modal-add-btn"
                    onClick={handleAddMembers}
                    disabled={addingMembers}
                  >
                    {addingMembers ? 'Adding…' : `Add ${selectedToAdd.length} Member${selectedToAdd.length !== 1 ? 's' : ''}`}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default GroupInfo;
